import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@column/ui';

const hintClassName =
  'pointer-events-none absolute left-1/2 z-[4] flex size-[22px] -translate-x-1/2 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-xs transition-opacity duration-300 [&_svg]:size-3.5';

const fadeClassName = 'pointer-events-none absolute inset-x-0 z-[3] h-8 transition-opacity duration-200';

interface Props {
  className?: string;
  scrollClassName?: string;
  contentClassName?: string;
  hintsOnEdges?: boolean;
  children: React.ReactNode;
}

export const VerticalScrollFade: React.FC<Props> = ({
  className,
  scrollClassName,
  contentClassName,
  hintsOnEdges,
  children,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;
    let frame = 0;
    let timeout: number | undefined;

    const commit = () => {
      frame = 0;
      const { scrollTop, scrollHeight, clientHeight } = element;
      const top = scrollTop > 2;
      const bottom = scrollTop + clientHeight < scrollHeight - 2;
      setEdges((current) => (current.top === top && current.bottom === bottom ? current : { top, bottom }));
    };
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(commit);
    };
    const reveal = () => {
      if (element.scrollHeight - element.clientHeight <= 2) return;
      setIsRevealed(true);
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => setIsRevealed(false), 1000);
    };

    commit();
    element.addEventListener('scroll', schedule, { passive: true });
    element.addEventListener('scroll', reveal, { passive: true });
    element.addEventListener('mouseenter', reveal);
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    if (contentRef.current) observer.observe(contentRef.current);

    return () => {
      element.removeEventListener('scroll', schedule);
      element.removeEventListener('scroll', reveal);
      element.removeEventListener('mouseenter', reveal);
      observer.disconnect();
      window.clearTimeout(timeout);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className={cn('relative', className)}>
      <div ref={scrollRef} className={cn('overflow-y-auto', scrollClassName)}>
        <div ref={contentRef} className={contentClassName}>
          {children}
        </div>
      </div>
      <div
        aria-hidden
        className={cn(fadeClassName, 'top-0 bg-[linear-gradient(to_bottom,var(--color-background),transparent)]')}
        style={{ opacity: edges.top ? 1 : 0 }}
      />
      <div
        aria-hidden
        className={cn(fadeClassName, 'bottom-0 bg-[linear-gradient(to_top,var(--color-background),transparent)]')}
        style={{ opacity: edges.bottom ? 1 : 0 }}
      />
      <span
        aria-hidden
        className={cn(hintClassName, 'top-0', hintsOnEdges && '-translate-y-1/2')}
        style={{ opacity: isRevealed && edges.top ? 1 : 0 }}
      >
        <ChevronUp />
      </span>
      <span
        aria-hidden
        className={cn(hintClassName, 'bottom-0', hintsOnEdges && 'translate-y-1/2')}
        style={{ opacity: isRevealed && edges.bottom ? 1 : 0 }}
      >
        <ChevronDown />
      </span>
    </div>
  );
};
